import { useState, useEffect, useCallback } from 'react';
import { apiPostLinkTokenCreate, apiPostItemPublicTokenExchange, apiPostItemGet, apiPostInstitutionsGetById } from '../api/plaid'
import { usePlaidLink } from 'react-plaid-link';
import { ButtonVariant } from '../components';
import Button from 'react-bootstrap/Button';
import Container from 'react-bootstrap/Container';
import PlaidItemManagement from './product-types/plaid-item-management';
import PlaidProducts from './product-types/plaid-products';
import PlaidItem from './item/plaid-item';
import Note from 'plaid-threads/Note';
import { logInfo, logError } from '../utils/utils';
import appConfig from '../app-config';

import './plaid-app.scss';

const PlaidApp = ({ userId }) => {

  const [updateMode, setUpdateMode] = useState(false);
  const [linkToken, setLinkToken] = useState(null);
  const [linkSuccess, setLinkSuccess] = useState(false);
  const [isItemAccess, setIsItemAccess] = useState(true);
  const [institution, setInstitution] = useState(true);

  const [accessToken, setAccessToken] = useState(null);
  const [itemId, setItemId] = useState(null);
  const [item, setItem] = useState([]);

  const [loading, setLoading] = useState(true);

  // 1) Generate or reuse a Link token
  const createLinkToken = useCallback(async () => {
    const isOauthRedirect = window.location.href.includes('?oauth_state_id=');

    if (isOauthRedirect) {
      const savedToken = localStorage.getItem('link_token');
      setLinkToken(savedToken);
    } else {
      const queryString = window.location.search;
      const urlParams = new URLSearchParams(queryString);
      const accessToken = urlParams.get('access_token');

      setUpdateMode(accessToken ? true : false)

      let linkTokenCreateData;
      if (accessToken) {
        // If refreshing an existing linked account, we need to pass consented 'liabilities' product
        // only if available for the given institution
        const itemData = await apiPostItemGet(accessToken);
        const institutionData = await apiPostInstitutionsGetById(itemData.item.institution_id)
        setInstitution(institutionData.institution)

        const consentedProducts = [];
        if (institutionData.institution.products.includes("liabilities"))
          consentedProducts.push("liabilities");

        linkTokenCreateData = await apiPostLinkTokenCreate(userId, consentedProducts, accessToken);
      } else {
        linkTokenCreateData = await apiPostLinkTokenCreate(userId, null, null);
      }

      setLinkToken(linkTokenCreateData.link_token);

      // Save the link_token to be used later in the Oauth flow.
      localStorage.setItem('link_token', linkTokenCreateData.link_token);
    }
  }, [userId]);

  // 5) Get information about this Item
  const getItem = useCallback(async (accessToken) => {
    setLoading(true);

    const itemData = await apiPostItemGet(accessToken);

    setItem(itemData.item)
    setIsItemAccess(true);

    setLoading(false);
  }, []);

  // 2) Handle successful link
  const onSuccess = useCallback(async (publicToken, metadata) => {
    // send public_token to your server
    logInfo(publicToken, metadata);

    setLoading(true);

    const itemPublicTokenExchangeData = await apiPostItemPublicTokenExchange(publicToken);
    setAccessToken(itemPublicTokenExchangeData.access_token);
    setItemId(itemPublicTokenExchangeData.item_id);
    setLinkSuccess(true);

    await getItem(itemPublicTokenExchangeData.access_token);
  }, [getItem]);

  const onEvent = useCallback(async (eventName, metadata) => {
    // log onEvent callbacks from Link
    logInfo(eventName, metadata);
  }, []);

  const onExit = useCallback(async (error, metadata) => {
    // log onExit callbacks from Link, handle errors
    logError(error, metadata);
  }, []);

  // 3) Setup Plaid Link config
  const isOauth = window.location.href.includes('?oauth_state_id=');
  const config = {
    token: linkToken,
    onSuccess,
    onEvent,
    onExit,
    ...(isOauth && { receivedRedirectUri: window.location.href }),
  };

  // 4) Launch Plaid Link
  const { open, ready } = usePlaidLink(config);

  useEffect(() => {
    if (!linkToken) {
      createLinkToken();
    }

    if (isOauth && ready) {
      open();
    }
  }, [linkToken, isOauth, ready, open, createLinkToken]);

  return (
    <>
      <Container className={'plaid-grid'}>
        <h3 className={'plaid-title'}>{appConfig.app.title}</h3>

        {!linkSuccess && (
          <>
            {updateMode && <Note className={'plaid-note'}>UPDATE MODE</Note>}
            <h4 className={'plaid-subtitle'}>Integration with Plaid</h4>
            {institution && institution.name && (
              <div className={'institution-info'}>
                <div className={'institution-details'}>
                  {institution.logo && (
                    <img
                      src={`data:image/png;base64,${institution.logo}`}
                      alt="Institution Logo"
                      className={'institution-logo'}
                    />
                  )}
                  <div>
                    <div>{institution.name}</div>
                    <div>{institution?.url && (
                      <a
                        href={institution.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ fontSize: '12px', color: '#6c757d' }}
                      >
                        {institution.url.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')}
                      </a>
                    )}
                    </div>
                  </div>
                </div>
                {institution.products && (
                  <div className={'institution-products'}>
                    <strong>Products:</strong> {institution.products.join(', ')}
                  </div>
                )}
              </div>
            )}

            <p className={'plaid-intro-paragraph'}>
              Click the button below to launch Link to connect your bank account to Plaid,
              which will allow you to access your accounts via the Plaid API.
            </p>

            <Button
              variant={ButtonVariant.DARK}
              onClick={open}
              disabled={!ready}
            >
              {ready ? 'Launch Link' : 'Loading...'}
            </Button>
          </>
        )}

        {linkSuccess && (
          <>
            {loading && (
              <p className={'plaid-requests'}>
                Loading...
              </p>
            )}
            {!loading && isItemAccess && (
              <>
                {updateMode && (
                  <>
                    <Note className={'plaid-note'}>UPDATE MODE</Note>
                    <h4 className={'plaid-subtitle'}>
                      Your <strong>{item.institution_name}</strong> account connection to Plaid has been refreshed!
                    </h4>
                  </>
                )}
                {!updateMode && (
                  <>
                    <PlaidItem
                      institutionId={item.institution_id}
                      institutionName={item.institution_name}
                      itemId={itemId}
                      accessToken={accessToken}
                    />
                    <p className={'plaid-requests'}>
                      Now that you have an access_token, you can make all of the following requests:
                    </p>
                    <PlaidItemManagement />
                    <PlaidProducts products={item.available_products} />
                  </>
                )}
              </>
            )}
          </>
        )}

      </Container>
    </>
  );
};

export default PlaidApp;